import React, { JSX } from "react";
import { Box, Typography } from "@mui/material";
import { round } from "mathjs";
import startCase from "lodash/startCase";
import moment from "moment";
import "../PrintBill.scss";
import { useSelector } from "react-redux";
import { BillItemType, BillType } from "../../../../interfaces/BillInterfaces";
import { RootState } from "../../../../store";
import "./PrintStyle.scss";
import InfoField from "./InfoField";
import { t } from "../../../../components/translate";
import capitalizedName from "../../../../helpers/nameCapitalizer";
import { roundOffAndDisplayInRupee } from "../../../../helpers/formatters";
import { scaleFont } from "../../../../components/Print/Print";
import { spFromIdSelector, spFullNameSelectorWithNmc } from "../../../../reducers/serviceProvider";
import { getPaymentMethodLabel } from "../../../Reports/SalesReport";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import { hasItemWithHSCode } from "../../../../actions/helpers/billHelper";
import { useShowStockBatchAndExpiryInBillPrint } from "../../../../hooks/useShowStockBatchAndExpiryInBillPrint";

enum EIGHTY_MM_COLUMNS {
  HS_CODE = "HS Code",
  DESCRIPTION = "Description",
  QTY = "Qty",
  RATE_PER_QTY = "Rate/Qty",
  VAT_PERCENT = "VAT %",
  AMOUNT = "Amount"
}

export const EightYMMColumns = [
  EIGHTY_MM_COLUMNS.HS_CODE,
  EIGHTY_MM_COLUMNS.DESCRIPTION,
  EIGHTY_MM_COLUMNS.QTY,
  EIGHTY_MM_COLUMNS.RATE_PER_QTY,
  EIGHTY_MM_COLUMNS.VAT_PERCENT,
  EIGHTY_MM_COLUMNS.AMOUNT
];

const ItemHeadings = ({ headers }: { headers: string[] }) => (
  <Box sx={{ display: "flex" }}>
    {headers.map((col) => {
      const flexGrow = col === EIGHTY_MM_COLUMNS.DESCRIPTION ? 1 : undefined;
      const flexBasis = col === EIGHTY_MM_COLUMNS.DESCRIPTION ? "3cm" : "50px";
      const justifyContent =
        col === EIGHTY_MM_COLUMNS.DESCRIPTION || col === EIGHTY_MM_COLUMNS.HS_CODE
          ? "flex-start"
          : "flex-end";

      return (
        <Box
          key={col}
          sx={{
            display: "flex",
            flexGrow,
            flexBasis,
            justifyContent
          }}
        >
          <InfoField valueStyle={{ fontWeight: "medium" }} value={col} />
        </Box>
      );
    })}
  </Box>
);

const BillItems = ({
  billItems,
  columns
}: {
  billItems: BillItemType[];
  columns: EIGHTY_MM_COLUMNS[];
}) => {
  const currentRc = useCurrentResourceCentre();
  const { showStockBatchAndExpiryInBillPrint } = useShowStockBatchAndExpiryInBillPrint();

  const showSubItemPriceOnPrint = currentRc?.settings?.billingSettings?.showSubItemPriceOnPrint;
  const SPs = useSelector((state: RootState) => state.resources.resourceCentreServiceProviders);
  const spField = (item: BillItemType) =>
    Array.isArray(item?.serviceProviderIds) && item.serviceProviderIds.length > 0 ? (
      <Box component="span" sx={{ ml: "4px", fontSize: "6px" }}>
        (
        {item.serviceProviderIds
          .map((spId) => spFullNameSelectorWithNmc(spFromIdSelector(SPs, spId)))
          .join(", ")}
        )
      </Box>
    ) : (
      // for older bills with single sp id
      item?.serviceProviderId && (
        <Box component="span" sx={{ ml: "4px", fontSize: "6px" }}>
          ({spFullNameSelectorWithNmc(spFromIdSelector(SPs, item.serviceProviderId))})
        </Box>
      )
    );

  return (
    <Box mb={1} mt={0.2} pt={0.2} borderBottom="0.5px solid #bbb" borderTop="0.5px solid #bbb">
      {[...billItems]
        .sort((a: BillItemType, b: BillItemType) => a.sNo - b.sNo)
        .map((item) => (
          <Box
            m="0.05cm 0cm"
            display="flex"
            key={item.uuid}
            alignItems="flex-start"
            justifyContent="flex-start"
          >
            {columns.map((col) => (
              <>
                {col === EIGHTY_MM_COLUMNS.HS_CODE && (
                  <Box sx={{ flexBasis: "50px" }}>
                    <InfoField value={item?.hsCode || "-"} />
                  </Box>
                )}
                {col === EIGHTY_MM_COLUMNS.DESCRIPTION && (
                  <Box
                    sx={{
                      fontSize: "0.2cm",
                      lineHeight: 1,
                      flexGrow: 1,
                      color: "#000",
                      flexBasis: "3cm"
                    }}
                  >
                    {item.description}
                    {(item?.teeth?.length ?? 0) > 0 && (
                      <Box component="span" sx={{ ml: "4px", fontSize: "6px" }}>
                        ( {`Teeth: ${item?.teeth?.join(", ")}`})
                      </Box>
                    )}
                    {/* INFO: here the sp name was not being shown previously, is it in purpose? */}
                    <Typography
                      fontSize={scaleFont("0.2cm", 0.75)}
                      component="i"
                      sx={{ wordBreak: "break-all" }}
                    >
                      {item.serviceCode && ` (${item.serviceCode})`}
                    </Typography>
                    {spField(item)}

                    {showStockBatchAndExpiryInBillPrint && (
                      <Typography
                        fontSize={scaleFont("0.2cm", 0.75)}
                        component="i"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {(item?.batchId || item.batchInfo?.batchId) &&
                          `Batch ${item?.batchId || item.batchInfo?.batchId}`}
                        {(item?.expiryDate || item.batchInfo?.expiryDate) &&
                          `, Exp ${moment(item?.expiryDate || item.batchInfo?.expiryDate).format(
                            "MMM YYYY"
                          )}`}
                      </Typography>
                    )}
                    {/* for sub items */}
                    {item?.subItems?.map((subitem) => (
                      <Box key={subitem.productId} paddingTop="0.05cm" ml="5px" fontSize="6px">
                        {`◦ ${subitem.description}`}
                        {spField(subitem)}
                      </Box>
                    ))}
                  </Box>
                )}
                {col === EIGHTY_MM_COLUMNS.QTY && (
                  <Box
                    sx={{
                      flexBasis: "50px",
                      display: "flex",
                      justifyContent: "flex-end"
                    }}
                  >
                    <InfoField value={item.quantity} />
                  </Box>
                )}
                {col === EIGHTY_MM_COLUMNS.RATE_PER_QTY && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      flexBasis: "50px",
                      flexDirection: "column"
                    }}
                  >
                    <InfoField value={Number(item.perUnit).toFixed(2)} />
                    {showSubItemPriceOnPrint &&
                      item?.subItems?.map((subitem) => (
                        <Box key={subitem.productId} ml="5px" fontSize="6px">
                          {`${subitem.productData?.grossTotalPrice}`}
                        </Box>
                      ))}
                  </Box>
                )}
                {col === EIGHTY_MM_COLUMNS.VAT_PERCENT && (
                  <Box
                    sx={{
                      flexBasis: "50px",
                      display: "flex",
                      justifyContent: "flex-end"
                    }}
                  >
                    <InfoField value={item?.vatPct ?? 0} />
                  </Box>
                )}
                {col === EIGHTY_MM_COLUMNS.AMOUNT && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      flexBasis: "50px"
                    }}
                  >
                    <InfoField value={Number(item.grossTotal).toFixed(2)} />
                  </Box>
                )}
              </>
            ))}
          </Box>
        ))}
    </Box>
  );
};

const Summary = ({
  bill,
  user,
  hidePaymentMethodOnPrint
}: {
  bill: BillType;
  user: { firstName: string; lastName: string };
  hidePaymentMethodOnPrint: boolean;
}): JSX.Element => {
  const dueAmount = round(bill.summary.totalAmount - bill.paymentInfo.paidAmount || 0, 2);
  const hasNewTaxSummary = bill?.summary && "vatAmtWithRates" in bill.summary;

  const showLegacyVat =
    Object.keys(bill?.document?.summary?.taxAmtWithRates || {}).filter(
      (vatPct) => Number(vatPct) !== 0
    ).length > 0;

  const showVatByRate =
    Object.keys(bill?.document?.summary?.vatAmtWithRates || {}).filter(
      (vatPct) => Number(vatPct) !== 0
    ).length > 0;

  return (
    <>
      {Boolean(bill.summary.totalPerUnitTimesQty) && (
        <InfoField
          valueStyle={{ fontWeight: "bold" }}
          label="Gross Total"
          value={roundOffAndDisplayInRupee(bill.summary.totalPerUnitTimesQty)}
        />
      )}

      {!hasNewTaxSummary &&
        showLegacyVat &&
        Object.keys(bill?.summary?.taxAmtWithRates || {})
          .filter((vatPct) => Number(vatPct) !== 0)
          .map((vatPct, index) => (
            <InfoField
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              label={<>VAT {vatPct}%</>}
              value={bill?.summary?.taxAmtWithRates?.[vatPct]?.toFixed(2) || ""}
            />
          ))}

      {hasNewTaxSummary &&
        showVatByRate &&
        Object.keys(bill?.summary?.vatAmtWithRates || {})
          .filter((vatPct) => Number(vatPct) !== 0)
          .map((vatPct, index) => (
            <InfoField
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              label={<>VAT {vatPct}%</>}
              value={bill?.summary?.vatAmtWithRates?.[vatPct]?.toFixed(2) || ""}
            />
          ))}

      {bill.summary.taxAmount > 0 && (
        <InfoField label="Tax Amount" value={roundOffAndDisplayInRupee(bill.summary.taxAmount)} />
      )}

      {bill.summary.discount > 0 && (
        <InfoField label="Discount" value={roundOffAndDisplayInRupee(bill.summary.discount)} />
      )}

      <InfoField
        valueStyle={{ fontWeight: "bold" }}
        label="Grand Total"
        value={roundOffAndDisplayInRupee(bill.summary.totalAmount)}
      />

      {bill?.document?.ssf?.id &&
        bill?.document?.paymentInfo?.paymentDistribution?.paymentFromSsf && (
          <InfoField
            valueStyle={{ fontWeight: "bold" }}
            label={t("billing.paymentFromSSF")}
            value={roundOffAndDisplayInRupee(
              bill.document.paymentInfo.paymentDistribution.paymentFromSsf
            )}
          />
        )}
      <InfoField
        valueStyle={{ fontWeight: "bold" }}
        label="Paid Amount"
        value={roundOffAndDisplayInRupee(bill.paymentInfo.paidAmount)}
      />
      {dueAmount > 0 && (
        <InfoField
          valueStyle={{ fontWeight: "bold" }}
          label="Due Amount"
          value={roundOffAndDisplayInRupee(dueAmount)}
        />
      )}
      {!!bill.paymentInfo?.tenderAmount && (
        <>
          <InfoField
            valueStyle={{ fontWeight: "bold" }}
            label={t("billing.tenderAmount")}
            value={round(bill.paymentInfo.tenderAmount, 2).toFixed(2)}
          />
          <InfoField
            valueStyle={{ fontWeight: "bold" }}
            label={t("billing.changeAmount")}
            value={round(bill.paymentInfo.changeAmount, 2).toFixed(2)}
          />
        </>
      )}
      <Box sx={{ margin: "0.1cm" }} />
      <InfoField value={`${bill.summary.inWords} only`} />
      <Box sx={{ margin: "0.1cm" }} />

      {!hidePaymentMethodOnPrint && (
        <InfoField
          label="Payment Method"
          value={startCase(
            getPaymentMethodLabel({
              paymentMethod: bill?.paymentInfo?.paymentMethod ?? null,
              paymentBreakdown: bill.paymentInfo.paymentBreakdown ?? []
            })
          )}
        />
      )}
      <InfoField
        label={t("billing.printedDateTime")}
        value={`${moment().format("YYYY/MM/DD")} ${moment().format("h:mm a")}`}
      />
      {bill?.printCount > 0 && user && (
        <InfoField
          label={t("billing.rePrintUser")}
          value={capitalizedName(`${user?.firstName} ${user?.lastName}`)}
        />
      )}
      {bill.document.enteredBy.name && (
        <InfoField label={t("billing.billedBy")} value={bill.document.enteredBy.name} />
      )}
    </>
  );
};

const BillDescription = ({
  billData,
  hidePaymentMethodOnPrint = false
}: {
  billData: BillType;
  hidePaymentMethodOnPrint?: boolean;
}): JSX.Element | null => {
  const user = useSelector((state: RootState) => state.userContext.user);
  const currentRc = useCurrentResourceCentre();

  if (!billData?.document?.billItems) return null;

  const showHSCode = hasItemWithHSCode(billData);
  const isVatRegistered = currentRc?.settings?.billingSettings.vatRegistered;

  const columns = EightYMMColumns.filter((header) => {
    if (header === EIGHTY_MM_COLUMNS.HS_CODE) {
      return showHSCode;
    }

    if (header === EIGHTY_MM_COLUMNS.VAT_PERCENT) {
      if (isVatRegistered) {
        return header;
      }
      return false;
    }

    return header;
  });

  return (
    <div>
      <ItemHeadings headers={columns} />
      <BillItems billItems={billData.document.billItems} columns={columns} />
      <Summary bill={billData} user={user} hidePaymentMethodOnPrint={hidePaymentMethodOnPrint} />
    </div>
  );
};

export default BillDescription;
